import React, { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import FooterOne from '../common/footer/FooterOne';
import HeaderOne from '../common/header/HeaderOne';
import CtaLayoutOne from '../component/cta/CtaLayoutOne';
import BlogData from "../data/blog/BlogData.json";
import BreadCrumbOne from '../elements/breadcrumb/BreadCrumbOne';
import BlogSidebar from '../component/blog/BlogSidebar';
import ColorSwitcher from '../elements/switcher/ColorSwitcher';
import SEO from '../common/SEO';
import { FaPlay, FaAngleRight, FaAngleLeft } from "react-icons/fa";
import FsLightbox from 'fslightbox-react';
import Slider from "react-slick";
import BlogListOne from '../component/blog/BlogListOne';
import ErrorPage from './404';
import BlogFaqAccordion, { splitFaqFromBody } from '../component/blog/BlogFaqAccordion';

const SITE_URL = 'https://itcodeinfotech.com';
const allBlogData = BlogData;

// post_date is stored as a human-readable string ("Oct 16, 2025") for the
// visible byline. JSON-LD and <time datetime> both require ISO 8601.
function toISODate(humanDate) {
    const parsed = new Date(humanDate);
    return isNaN(parsed) ? undefined : parsed.toISOString().slice(0, 10);
}


const BlogDetails = () => {

    const params = useParams();
    const slug = params.slug;

    const getBlogData = allBlogData.filter(blog => blog.slug === slug);
    const detailsBlog = getBlogData[0];

    const [toggler, setToggler] = useState(false);

    const { before: bodyBeforeFaq, heading: faqHeading, items: faqItems, after: bodyAfterFaq } =
        useMemo(() => splitFaqFromBody(detailsBlog && detailsBlog.body), [detailsBlog]);

    if (!detailsBlog) {
        return <ErrorPage />;
    }

    function SlickNextArrow(props) {
        const { className, onClick } = props;
        return (
          <div className={className} onClick={onClick}><FaAngleRight /></div>
        );
    }

    function SlickPrevArrow(props) {
        const { className, onClick } = props;
        return (
          <div className={className} onClick={onClick}><FaAngleLeft /></div>
        );
    }

    var slideSettings = {
        dots: false,
        infinite: false,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        nextArrow: <SlickNextArrow />,
        prevArrow: <SlickPrevArrow />,
       
      };

    const postUrl = `${SITE_URL}/blog-details/${detailsBlog.slug}`;
    const thumbUrl = `${SITE_URL}/${Array.isArray(detailsBlog.large_thumb) ? detailsBlog.large_thumb[0] : detailsBlog.large_thumb}`;
    const blogPostingSchema = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": detailsBlog.title,
        "description": detailsBlog.excerpt,
        "image": thumbUrl,
        "author": {
            "@type": "Person",
            "name": detailsBlog.author_name
        },
        "publisher": {
            "@type": "Organization",
            "name": "ITCODE InfoTech",
            "logo": {
                "@type": "ImageObject",
                "url": `${SITE_URL}/images/logo.svg`
            }
        },
        "datePublished": toISODate(detailsBlog.post_date),
        "dateModified": toISODate(detailsBlog.post_date),
        "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": postUrl
        }
    };

    return (
        <>
            <SEO
                metaTitle={`${detailsBlog.title} | ITCODE InfoTech Blog`}
                metaDescription={detailsBlog.excerpt}
                canonicalTag={postUrl}
                ogTitle={detailsBlog.title}
                ogDescription={detailsBlog.excerpt}
                ogType="article"
                ogImage={thumbUrl}
                ogUrl={postUrl}
            />
            <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify(blogPostingSchema)}
                </script>
            </Helmet>
            <ColorSwitcher />
            <main className="main-wrapper">
                <HeaderOne />
                <BreadCrumbOne 
                title={detailsBlog.title}
                page="Blog"
                />
                <div className="section-padding-equal pb-0">
                    <div className="container">
                        <div className="row row-40">
                            <div className="col-lg-8">
                                <div className="single-blog">
                                    <div className="single-blog-content blog-grid">
                                        <div className="post-thumbnail">
                                            {
                                                Array.isArray(detailsBlog.large_thumb) ?
                                                <Slider {...slideSettings} className="slick-arrow-nav">
                                                    {detailsBlog.large_thumb.map((img, index) => (
                                                        <div className="slide-item" key={index}>
                                                            <img src={`${process.env.PUBLIC_URL}/${img}`} alt={`${detailsBlog.title} — ${index + 1}`} />
                                                        </div>
                                                    ))}

                                                </Slider>
                                                : <img src={`${process.env.PUBLIC_URL}/${detailsBlog.large_thumb}`} alt={detailsBlog.title} />
                                            }
                                           
                                            {detailsBlog.format === "video" ?
                                                <>
                                                    <div className="popup-video">
                                                        <button className="play-btn" onClick={ () => setToggler(!toggler) }><FaPlay /></button>
                                                    </div> 
                                                    <FsLightbox toggler={ toggler } sources={ ['https://www.youtube.com/watch?v=1iIZeIy7TqM'] }/>
                                                </> 
                                                : ""
                                            }

                                        </div>
                                        <div className="author">
                                            <div className="author-thumb">
                                                <img src={`${process.env.PUBLIC_URL}/${detailsBlog.author_avatar}`} alt={detailsBlog.author_name} />
                                            </div>
                                            <div className="info">
                                                <h6 className="author-name">
                                                    <Link to={process.env.PUBLIC_URL + "/about-us"}>{detailsBlog.author_name}</Link>
                                                </h6>
                                                <ul className="blog-meta list-unstyled">
                                                    <li><time dateTime={toISODate(detailsBlog.post_date)}>{detailsBlog.post_date}</time></li>
                                                </ul>
                                            </div>
                                        </div>
                                        {bodyBeforeFaq.map((data, i) =>(
                                            <div key={`before-${i}`} dangerouslySetInnerHTML={{__html: data}}></div>
                                        ))}
                                        {faqItems.length === 0 && bodyAfterFaq.map((data, i) =>(
                                            <div key={`after-${i}`} dangerouslySetInnerHTML={{__html: data}}></div>
                                        ))}

                                        {faqItems.length === 0 && (
                                            <div className="row">
                                                {detailsBlog.features_img.map((img, i) => (
                                                    <div className="col-6" key={i}>
                                                        <div className="featured-img">
                                                            <img src={process.env.PUBLIC_URL + img} alt={`${detailsBlog.title} — detail ${i + 1}`} />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {/* <BlogAuthor data={detailsBlog} /> */}
                                {/* <Comment /> */}

                            </div>
                            <div className="col-lg-4">
                                <BlogSidebar activeCategory={detailsBlog.category} />
                            </div>
                        </div>
                    </div>
                </div>

                {faqItems.length > 0 && (
                    <>
                        <BlogFaqAccordion heading={faqHeading} items={faqItems} />
                        <div className="section-padding-equal pt-0 pb-0">
                            <div className="container">
                                <div className="row row-40">
                                    <div className="col-lg-8">
                                        <div className="single-blog">
                                            <div className="single-blog-content blog-grid">
                                                {bodyAfterFaq.map((data, i) =>(
                                                    <div key={`after-${i}`} dangerouslySetInnerHTML={{__html: data}}></div>
                                                ))}
                                                <div className="row">
                                                    {detailsBlog.features_img.map((img, i) => (
                                                        <div className="col-6" key={i}>
                                                            <div className="featured-img">
                                                                <img src={process.env.PUBLIC_URL + img} alt={`${detailsBlog.title} — detail ${i + 1}`} />
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                <div className="section section-padding-equal pt-0 related-blog-area">
                    <div className="container">
                        <div className="section-heading heading-left">
                            <h3 className="title">Related Post</h3>
                        </div>
                        <div className="row g-4">
                            <BlogListOne colSize="col-xl-6" itemShow="2" category={detailsBlog.category} excludeId={detailsBlog.id} />
                        </div>
                    </div>
                </div>
                <CtaLayoutOne />
            <FooterOne parentClass="" />
            </main>
        </>
    )
}

export default BlogDetails;